import type { ReactElement, ReactNode } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useCopiedNotification } from "app/pages/sales-intelligence/hooks/useCopiedNotification";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";

import { CONTACT_TYPE_TO_ICON } from "../../constants/contact-type-to-icon";
import type { ContactEntryType } from "../../types/common";

import { StyledContactCopyIconButton, StyledContactDetailWrapperFlex, StyledContactItemText } from "./styles";

interface IProps {
    isRevealed: boolean;
    isInvalid?: boolean;
    contact: string;
    type: ContactEntryType;
    dataAutomation?: string;
    infoTooltip?: ReactNode;
    infoIconName?: "block" | "info";
    infoIconColor?: string;
    onCopy?: () => void;
}

const ContactDetailItem = ({
    isRevealed,
    isInvalid,
    contact,
    type,
    dataAutomation,
    infoTooltip,
    infoIconName = "info",
    infoIconColor = colorsPalettes.carbon[200],
    onCopy: onCopyProp,
}: IProps): ReactElement => {
    const { isCopied, onCopy } = useCopiedNotification();

    const copyToClipboard = (): void => {
        trackingService.trackContactDetailsCopy();
        void navigator.clipboard.writeText(contact);
        onCopy();
        onCopyProp?.();
    };

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="10px"
            width="100%"
            data-automation={dataAutomation}
        >
            <StyledContactDetailWrapperFlex
                alignItems="center"
                gap="9px"
            >
                <SWReactIcons
                    iconName={CONTACT_TYPE_TO_ICON[type]}
                    size="12px"
                    iconColor={colorsPalettes.carbon[500]}
                />
                <StyledContactItemText
                    isBlured={!isRevealed}
                    fontVariant="sectionTitle"
                    color={colorsPalettes.carbon[400]}
                    onCopy={() => trackingService.trackContactDetailsCopy()}
                >
                    {contact}
                </StyledContactItemText>

                {infoTooltip ? (
                    <Tooltip content={infoTooltip}>
                        <SWReactIcons
                            iconName={infoIconName}
                            size="xs"
                            iconColor={infoIconColor}
                        />
                    </Tooltip>
                ) : null}
            </StyledContactDetailWrapperFlex>

            {isRevealed && !isInvalid ? (
                <Tooltip content={translate(isCopied ? "si.common.copied" : "si.common.copy")}>
                    <StyledContactCopyIconButton
                        className="visibleOnRowHover"
                        type="flat"
                        width={20}
                        height={20}
                        iconName={isCopied ? "checked" : "copy-rounded"}
                        iconSize="14px"
                        onClick={copyToClipboard}
                    />
                </Tooltip>
            ) : null}
        </Flex>
    );
};

export default ContactDetailItem;
